import { useState, useMemo } from "react";

export type SortDirection = "asc" | "desc";

export interface SortConfig<T> {
  field: keyof T | null;
  direction: SortDirection | null;
}

export interface UseSortOptions<T> {
  initialField?: keyof T;
  initialDirection?: SortDirection;
  currencyFields?: (keyof T)[];
}

export interface UseSortReturn<T> {
  sortedData: T[];
  sortConfig: SortConfig<T>;
  handleSort: (field: keyof T) => void;
}

const parseCurrency = (value: string): number => {
  return parseFloat(value.replace(/[^0-9.-]+/g, ""));
};

export const useTableSort = <T extends Record<string, any>>(
  data: T[],
  options: UseSortOptions<T> = {}
): UseSortReturn<T> => {
  const { initialField = null, initialDirection = null, currencyFields = [] } = options;

  const [sortConfig, setSortConfig] = useState<SortConfig<T>>({
    field: initialField,
    direction: initialDirection,
  });

  const sortedData = useMemo(() => {
    if (!sortConfig.field || !sortConfig.direction) {
      return data;
    }

    const sorted = [...data].sort((a, b) => {
      const aValue = a[sortConfig.field!];
      const bValue = b[sortConfig.field!];

      let comparison = 0;

      if (currencyFields.includes(sortConfig.field!)) {
        const aNum = parseCurrency(String(aValue));
        const bNum = parseCurrency(String(bValue));
        comparison = aNum - bNum;
      } else if (typeof aValue === "number" && typeof bValue === "number") {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return sortConfig.direction === "asc" ? comparison : -comparison;
    });

    return sorted;
  }, [data, sortConfig, currencyFields]);

  const handleSort = (field: keyof T) => {
    setSortConfig((prevConfig) => {
      if (prevConfig.field === field) {
        if (prevConfig.direction === "asc") {
          return { field, direction: "desc" };
        } else if (prevConfig.direction === "desc") {
          return { field, direction: "asc" };
        }
      }
      return { field, direction: "asc" };
    });
  };

  return {
    sortedData,
    sortConfig,
    handleSort,
  };
};

